/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect, useState} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {Activation} from '../modules/auth/components/Activation'
import {ForgotPassword} from '../modules/auth/components/ForgotPassword'
import CreateCompany from '../modules/admin/Company/Forms'
import {CreateNewPassword} from 'app/modules/auth/components/CreateNewPassword'

// import Home from '../../thesis-and-dissertations/pages/Home'
import Content from '../../thesis-and-dissertations/pages/Content'
import FlipBook from '../../flip-book/pages/Book/index'
import ContentSearch from '../../thesis-and-dissertations/pages/ContentSearch'
import ContentDetail from '../../thesis-and-dissertations/pages/ContentDetail'
import Profile from '../../thesis-and-dissertations/pages/Profile'
import ThesisAuth from '../../thesis-and-dissertations/pages/ThesisAuth'
import ResearchType from 'thesis-and-dissertations/pages/ResearchType'
import ResearchDetail from 'thesis-and-dissertations/pages/ResearchType/Detail'
import AlumniDashboard from 'pana/pages/Home'

// CoP
import COPHome from 'CoP/pages/home'
import COPDetail from 'CoP/pages/detail'
import ReactGA from 'react-ga4'
import {BackdoorLogin} from 'app/modules/auth/components/BackdoorLogin'

// New pages
import LandingPage from 'landing-page/pages/Home'
import Blog from 'landing-page/pages/Blog'
import BlogDetail from 'landing-page/pages/Blog/Detail'
import Alumni from 'landing-page/pages/Alumni'
import ProfileAlumni from 'landing-page/pages/Profile'
import History from 'landing-page/pages/History'

const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  // Use state to make routing reactive to localStorage changes
  const [currentUser, setCurrentUser] = useState<string | null>(
    () => localStorage.getItem('kt-auth-react-v')
  )
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('role'))

  useEffect(() => {
    ReactGA.send({hitType: 'pageview', page: window.location.pathname})
  }, [])

  // Listen to storage events to update state when auth changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentUser(localStorage.getItem('kt-auth-react-v'))
      setRole(localStorage.getItem('role'))
    }

    // Listen to storage events (for cross-tab updates)
    window.addEventListener('storage', handleStorageChange)
    
    // Listen to custom auth-storage-changed event (for same-tab updates)
    window.addEventListener('auth-storage-changed', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-storage-changed', handleStorageChange)
    }
  }, [])

  // Check localStorage directly on each render to ensure we have the latest value
  // This handles the case where state hasn't updated yet after login
  const authFromStorage = localStorage.getItem('kt-auth-react-v')
  const roleFromStorage = localStorage.getItem('role')
  const isAuthenticated = authFromStorage || currentUser

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />

          {isAuthenticated ? (
            <>
              {(roleFromStorage === 'Alumni' || role === 'Alumni') && (
                <Route path='dashboard' element={<AlumniDashboard />} />
              )}

              {/* Semua route user login */}
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
              <Route path='backdoor-login' element={<BackdoorLogin />} />
            </>
          ) : (
            <>
              {/* Semua route user belum login */}
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='auth/:isCompany' element={<AuthPage />} />
              <Route path='thesis/:page' element={<ThesisAuth />} />
              <Route path='profile-user' element={<Profile />} />
              {/* COMMENTED: Research type selection - not needed without login */}
              {/* <Route path='research-type/:type' element={<ResearchDetail />} /> */}
              {/* <Route path='research-type' element={<ResearchType />} /> */}
              <Route path='content' element={<Content />} />
              <Route path='flip-book/:id' element={<FlipBook />} />
              <Route path='content-search' element={<ContentSearch />} />
              <Route path='content/:id' element={<ContentDetail />} />
              <Route path='activation/*' element={<Activation />} />
              <Route path='forgot-password/*' element={<ForgotPassword />} />
              <Route path='create-new-password/:code' element={<CreateNewPassword />} />
              <Route path='company-register' element={<CreateCompany />} />
              <Route path='cop' element={<COPHome />} />
              <Route path='cop/:id' element={<COPDetail />} />
              <Route path='blog' element={<Blog />} />
              <Route path='blog/:id' element={<BlogDetail />} />
              <Route path='alumni' element={<Alumni />} />
              <Route path='ajukan-profil' element={<ProfileAlumni />} />
              <Route path='riwayat-pengajuan' element={<History />} />

              {/* Default landing page */}
              <Route index element={<LandingPage />} />

              {/* 🔸 404 hanya untuk user belum login */}
              <Route path='*' element={<Navigate to='/error/404' replace />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
